import type { UnitSystem } from "@/atoms/units";
import type { SurfRating } from "@/lib/conditions";
import { fontStack } from "@/theme";
import { convertHeight, heightUnit } from "@/lib/units";
import { Box, Typography, useTheme } from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useState } from "react";

interface ConditionsHeadlineProps {
  spotLabel: string;
  waveHeightM: number;
  rating: SurfRating;
  /** One-line prose summary of the conditions. Parent computes it
   *  from the same inputs the rater used so the reason stays honest
   *  (e.g. "Too small to surf" vs "Blown out"). */
  descriptor: string;
  dataUpdatedAt: number;
  units: UnitSystem;
}

/**
 * Editorial headline for the current spot — the first thing on the page.
 * DESIGN.md calls for print-editorial restraint: serif spot name, mono
 * hero readout, a terse prose summary. No color chrome, no emoji.
 */
export default function ConditionsHeadline({
  spotLabel,
  waveHeightM,
  rating,
  descriptor,
  dataUpdatedAt,
  units,
}: ConditionsHeadlineProps) {
  const theme = useTheme();
  const updatedLabel = useRelativeTime(dataUpdatedAt);
  const height = convertHeight(waveHeightM, units).toFixed(1);

  // Kelp / buoy only for the extremes; everything else stays neutral ink.
  // (DESIGN.md: one accent, plus kelp/buoy used sparingly.)
  const descriptorColor =
    rating === "EPIC" || rating === "GOOD"
      ? theme.palette.kelp.main
      : rating === "FLAT" || rating === "POOR"
        ? theme.palette.buoy.main
        : theme.palette.ink.main;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        component="h1"
        sx={{
          fontFamily: fontStack.serif,
          fontSize: { xs: "1.625rem", md: "2rem" },
          lineHeight: 1.1,
          letterSpacing: "-0.02em",
          fontWeight: 500,
          color: "ink.main",
          mb: 0.75,
        }}
      >
        {spotLabel}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: 0.25 }}>
        <Typography
          component="span"
          sx={{
            fontFamily: fontStack.mono,
            fontSize: { xs: "2.75rem", md: "3.5rem" },
            lineHeight: 1,
            letterSpacing: "-0.03em",
            fontWeight: 500,
            color: "ink.main",
          }}
        >
          {height}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontFamily: fontStack.mono,
            fontSize: { xs: "1.125rem", md: "1.375rem" },
            color: "ink.muted",
            fontWeight: 500,
          }}
        >
          {heightUnit(units)}
        </Typography>
      </Box>

      <Typography
        sx={{
          fontSize: "0.9375rem",
          color: descriptorColor,
          mb: 0.5,
        }}
      >
        {descriptor}
      </Typography>

      {dataUpdatedAt > 0 && (
        <Typography
          variant="overline"
          component="div"
          sx={{ color: "ink.muted", fontSize: "0.6875rem" }}
        >
          Updated {updatedLabel}
        </Typography>
      )}
    </Box>
  );
}

function useRelativeTime(timestamp: number): string {
  const [label, setLabel] = useState(() => formatLabel(timestamp));
  useEffect(() => {
    setLabel(formatLabel(timestamp));
    const id = setInterval(() => setLabel(formatLabel(timestamp)), 30_000);
    return () => clearInterval(id);
  }, [timestamp]);
  return label;
}

function formatLabel(timestamp: number): string {
  // `dataUpdatedAt` is 0 until the first settled fetch. Don't claim
  // "just now" for a render that happened before any data arrived.
  if (!timestamp) return "—";
  const diff = Date.now() - timestamp;
  if (diff < 60_000) return "just now";
  return `${formatDistanceToNow(timestamp)} ago`;
}
